// Common IAM policy starting points for the Create Policy form. Each chip
// prefills the JSON editor with a ready-made document the customer can then
// tweak — mirrors the "Quick templates" affordance on the Healthcheck monitor
// form. `*` resources apply to every bucket; the single-bucket template uses a
// placeholder ARN the user edits.

export interface PolicyTemplate {
  id: string;
  label: string;
  description: string;
  /** Suggested policy name — applied only when the name field is still blank. */
  suggestedName: string;
  /** Pretty-printed JSON document. */
  document: string;
}

/** Build a pretty-printed policy document from one allow statement. */
function doc(actions: string[], resources: string[]): string {
  return JSON.stringify(
    { Version: '2012-10-17', Statement: [{ Effect: 'Allow', Action: actions, Resource: resources }] },
    null,
    2,
  );
}

const ALL = ['arn:aws:s3:::*', 'arn:aws:s3:::*/*'];

export const POLICY_TEMPLATES: PolicyTemplate[] = [
  {
    id: 'read-only',
    label: 'Read-only',
    description: 'Get objects and list every bucket — no writes.',
    suggestedName: 'read-only',
    document: doc(['s3:GetObject', 's3:ListBucket', 's3:GetBucketLocation'], ALL),
  },
  {
    id: 'read-write',
    label: 'Read & write',
    description: 'Get, put and delete objects across all buckets.',
    suggestedName: 'read-write',
    document: doc(
      ['s3:GetObject', 's3:PutObject', 's3:DeleteObject', 's3:ListBucket', 's3:GetBucketLocation'],
      ALL,
    ),
  },
  {
    id: 'full-access',
    label: 'Full access',
    description: 'Every S3 action on every bucket.',
    suggestedName: 'full-access',
    document: doc(['s3:*'], ALL),
  },
  {
    id: 'list-buckets',
    label: 'List buckets only',
    description: 'List buckets and their location — no object access.',
    suggestedName: 'list-buckets',
    document: doc(['s3:ListAllMyBuckets', 's3:GetBucketLocation'], ['arn:aws:s3:::*']),
  },
  {
    id: 'single-bucket',
    label: 'Single bucket (read & write)',
    description: 'Full object access scoped to one bucket — edit the bucket name.',
    suggestedName: 'bucket-access',
    document: doc(
      ['s3:GetObject', 's3:PutObject', 's3:DeleteObject', 's3:ListBucket'],
      ['arn:aws:s3:::example-bucket', 'arn:aws:s3:::example-bucket/*'],
    ),
  },
];
